import { apiClient, withTenantId } from './client';
import type { AxiosResponse } from 'axios';
import type { DocType, FileType, Mapping } from '@ute/shared/types';

// Re-export shared types for convenience
export type { DocType, FileType } from '@ute/shared/types';

// Frontend-specific interface extending the shared Mapping type
// This matches the mapper-api response format
export interface MappingTemplate extends Omit<Mapping, 'files' | 'template' | 'validationFunction'> {
  name: string; // Make name required for frontend
  type: DocType; // Document type (e.g., 'po', 'inv', 'receipt', etc.)
  fileType: FileType; // File format
  status: 'NEW' | 'AUTO_MAPPING' | 'IN_PROGRESS' | 'COMPLETED' | 'IN_VERIFICATION' | 'VERIFIED';
  tenantId: string; // Make tenantId required for frontend
  metadata?: Record<string, any>;
  // labels inherited from Mapping
}

export interface MappingListResponse {
  data: MappingTemplate[];
  count: number;
  pagination?: {
    total: number;
    max: number;
    offset: number;
    hasMore: boolean;
  };
  nextToken?: string; // For backward compatibility
}

// Backend returns MappingTemplate directly (not wrapped)
export type MappingDetailResponse = MappingTemplate;

export interface CreateMappingRequest {
  name: string;
  type: DocType; // Document type (e.g., 'po', 'inv', 'receipt', etc.)
  fileType: FileType; // File format
  file: File;
  metadata?: Record<string, any>;
}

export interface UpdateMappingRequest {
  name?: string;
  status?: 'NEW' | 'AUTO_MAPPING' | 'IN_PROGRESS' | 'COMPLETED' | 'IN_VERIFICATION' | 'VERIFIED';
  metadata?: Record<string, any>;
}

export interface ExtractDataRequest {
  file: File;
  keys: string[];
}

export interface ExtractDataResponse {
  data: Record<string, any>;
  confidence: Record<string, number>;
}

export interface LabRecord {
  hash: string;
  mappingKey: string;
  targetTemplateKey: string;
  tenantId: string;
  name: string;
  workflowStatus: string;
  decision: string | null;
  structuralSimilarity: number | null;
  originalScore: { overall: number; breakdown?: Record<string, number>; issues?: string[] } | null;
  refinedScore: { overall: number; breakdown?: Record<string, number>; issues?: string[] } | null;
  appliedPatch: Record<string, { from: unknown; to: unknown }> | null;
}

export interface LabRecordDetail {
  beforeConfig: Record<string, any>;
  afterConfig: Record<string, any>;
  pdfUrl: string | null;
  pdfWidth: number | null;
  pdfHeight: number | null;
  originalExtraction: Record<string, { data: any[] }> | null;
  refinedExtraction: Record<string, { data: any[] }> | null;
  lineItemColumns: { fieldName: string; displayName: string }[] | null;
}

// Mapping API service
export const mappingApi = {
  /**
   * List all mapping templates
   */
  list: async (params?: {
    limit?: number;
    nextToken?: string;
    type?: string; // Filter by document type (e.g., 'po', 'inv')
    fileType?: string; // Filter by file format (e.g., 'PDF', 'EXCEL')
  }): Promise<AxiosResponse<MappingListResponse>> => {
    return apiClient.get('/mapping/list', { params });
  },

  /**
   * List PDF mapping templates from legacy PDFMapper
   */
  listPdf: async (params?: {
    max?: number;
    offset?: number;
    name?: string;
    key?: string;
    labels?: string;
    status?: string | string[];
    docType?: string; // 'po', 'inv', etc.
    creator?: string;
    owner?: string;
    sort?: string;
    order?: 'asc' | 'desc';
  }): Promise<AxiosResponse<MappingListResponse>> => {
    return apiClient.get('/pdfmapper/list', { params });
  },

  /**
   * Get a specific mapping template by key
   */
  get: async (key: string): Promise<AxiosResponse<MappingDetailResponse>> => {
    return apiClient.get(`/pdfmapper/${encodeURIComponent(key)}`);
  },

  /**
   * Create a new mapping template
   */
  create: async (
    request: CreateMappingRequest
  ): Promise<AxiosResponse<MappingDetailResponse>> => {
    const formData = new FormData();
    formData.append('file', request.file);
    formData.append('name', request.name);
    formData.append('type', String(request.type));
    formData.append('fileType', String(request.fileType));
    if (request.metadata) {
      formData.append('metadata', JSON.stringify(request.metadata));
    }

    return apiClient.post('/mapping', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  /**
   * Update an existing mapping template
   */
  update: async (
    key: string,
    request: UpdateMappingRequest
  ): Promise<AxiosResponse<MappingDetailResponse>> => {
    return apiClient.put(`/mapping/${encodeURIComponent(key)}`, request);
  },

  /**
   * Delete a mapping template
   */
  delete: async (key: string): Promise<AxiosResponse<void>> => {
    return apiClient.delete(`/mapping/${encodeURIComponent(key)}`);
  },

  /**
   * Extract data from a document using mapping template
   * Routes to appropriate endpoint based on file type
   */
  extract: async (
    request: ExtractDataRequest
  ): Promise<AxiosResponse<ExtractDataResponse>> => {
    const formData = new FormData();
    formData.append('file', request.file);
    request.keys.forEach((key) => formData.append('keys[]', key));

    // For now, use /mapping/extract for all types
    // TODO: Route PDFs to /pdfmapper/extract when needed
    return apiClient.post('/mapping/extract', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  /**
   * Verify PDF mapping template (PDF-specific endpoint)
   */
  verifyPdf: async (
    key: string,
    verificationData: {
      dexRuleSetId: string;
      pdfHash: string;
      data: Record<string, any>;
    },
    tenantId?: string
  ): Promise<AxiosResponse<MappingDetailResponse>> => {
    return apiClient.post(`/pdfmapper/${encodeURIComponent(key)}/verify`, verificationData, withTenantId(tenantId));
  },

  /**
   * Test PDF template (PDF-specific endpoint, requires file upload)
   */
  testPdf: async (
    key: string,
    testData?: any
  ): Promise<AxiosResponse<any>> => {
    return apiClient.post(`/pdfmapper/${encodeURIComponent(key)}/test`, testData || {});
  },

  /**
   * Test template using a PDF already in S3, identified by hash.
   * MAPPER-only. Returns { extraction, text }.
   */
  testByHash: async (
    key: string,
    pdfHash: string,
    tenantId?: string
  ): Promise<AxiosResponse<{ extraction: any; text: string }>> => {
    return apiClient.post(`/pdfmapper/${encodeURIComponent(key)}/test-by-hash`, { pdfHash }, withTenantId(tenantId));
  },

  /**
   * Test a candidate template (unsaved changes) against an existing PDF in S3.
   * Uses the legacy non-destructive test-candidate endpoint.
   * MAPPER-only.
   */
  testTemplateCandidate: async (
    key: string,
    candidateData: Record<string, any>,
    tenantId?: string
  ): Promise<AxiosResponse<{ prevVerifiedOk?: boolean; data?: any[]; meta?: any; [k: string]: any }>> => {
    return apiClient.post(
      `/pdfmapper/${encodeURIComponent(key)}/test-candidate`,
      { data: candidateData },
      withTenantId(tenantId)
    );
  },

  /**
   * Fetch raw PDF text for a stored PDF. Used by pattern editor for client-side match-count.
   * MAPPER-only.
   */
  getPdfText: async (
    key: string,
    pdfHash: string,
    tenantId?: string
  ): Promise<AxiosResponse<{ text: string }>> => {
    return apiClient.get(`/pdfmapper/${encodeURIComponent(key)}/pdf-text/${pdfHash}`, withTenantId(tenantId));
  },

  /**
   * Get presigned URL for uploading/downloading files
   */
  getPresignedUrl: async (params: {
    key: string;
    operation: 'upload' | 'download';
  }): Promise<AxiosResponse<{ url: string; expiresIn: number }>> => {
    return apiClient.get('/mapping/presigned-url', { params });
  },

  /**
   * Get the original document file URL for a mapping
   */
  getDocumentUrl: async (key: string): Promise<AxiosResponse<{ url: string }>> => {
    return apiClient.get(`/mapping/${encodeURIComponent(key)}/document-url`);
  },

  /**
   * Get extracted data from a mapping template
   * Returns the extracted field values from the template
   */
  getExtractedData: async (key: string): Promise<AxiosResponse<{
    header: Record<string, any>;
    lineItems: Record<string, any>[];
  }>> => {
    return apiClient.get(`/mapping/${encodeURIComponent(key)}/extracted-data`);
  },

  /**
   * Claim a mapping for verification (sets status to IN_PROGRESS or IN_VERIFICATION).
   * Pass claim=false to release it back to NEW/COMPLETED.
   */
  claimMapping: async (
    key: string,
    claim: boolean,
    tenantId?: string
  ): Promise<AxiosResponse<{ status: string; message?: string }>> => {
    const route = claim ? 'start-verification' : 'release-verification';
    return apiClient.post(
      `/pdfmapper/${encodeURIComponent(key)}/${route}`,
      {},
      withTenantId(tenantId)
    );
  },

  /**
   * Get mapping data for verification
   * Returns fields, test data, and date format for verification UI
   */
  getMappingData: async (key: string, hash: string, tenantId?: string): Promise<AxiosResponse<{
    status: string;
    fields: Record<string, any>;
    dateFormat: string;
    data: any;
  }>> => {
    return apiClient.get(
      `/pdfmapper/${encodeURIComponent(key)}/mapping-data/${encodeURIComponent(hash)}`,
      withTenantId(tenantId)
    );
  },

  /**
   * Lab API methods
   */

  /**
   * List all re-adjust records eligible for Lab analysis (single-key, have reAdjustContext).
   */
  labListRecords: async (nextKey?: string): Promise<AxiosResponse<{ records: LabRecord[]; nextKey: string | null }>> => {
    return apiClient.get('/pdfmapper/lab/records', { params: nextKey ? { nextKey } : undefined });
  },

  labGetRecord: async (hash: string): Promise<AxiosResponse<LabRecordDetail>> => {
    return apiClient.get(`/pdfmapper/lab/records/${encodeURIComponent(hash)}`);
  },

  /**
   * Test a candidate template config (before or after) against the lab PDF.
   * Uses the non-destructive test-candidate endpoint.
   */
  labTestCandidate: async (
    key: string,
    candidateData: Record<string, any>,
    tenantId?: string
  ): Promise<AxiosResponse<{ prevVerifiedOk?: boolean; data?: any[]; meta?: any }>> => {
    return apiClient.post(
      `/pdfmapper/${encodeURIComponent(key)}/test-candidate`,
      { data: candidateData },
      withTenantId(tenantId)
    );
  },

  /**
   * DocType API methods
   */

  /**
   * List all DocTypes
   */
  listDocTypes: async (): Promise<AxiosResponse<Array<{
    id: string;
    name: string;
    parent?: string;
  }>>> => {
    return apiClient.get('/pdfmapper/doctypes');
  },

  /**
   * Get a specific DocType by ID
   */
  getDocType: async (id: string, tenantId?: string): Promise<AxiosResponse<{
    id: string;
    name: string;
    script: string;
    parent?: string;
    dataFields: any[];
    options: any[];
  }>> => {
    return apiClient.get(`/pdfmapper/doctype/${encodeURIComponent(id)}`, withTenantId(tenantId));
  },

  /**
   * Update a DocType
   */
  /**
   * Update mapping template data (coords, patterns, dateFormat, locale, etc.).
   * MAPPER-only. Regression-guarded by legacy update().
   * Pass force=true to bypass the guard (e.g. different-document scenario).
   */
  updateTemplate: async (
    key: string,
    data: Record<string, any>,
    tenantId?: string,
    force = false
  ): Promise<AxiosResponse<any>> => {
    return apiClient.put(
      `/pdfmapper/${encodeURIComponent(key)}/template`,
      { data, force },
      withTenantId(tenantId)
    );
  },

  updateDocType: async (id: string, data: {
    name?: string;
    script?: string;
    parent?: string;
  }): Promise<AxiosResponse<{
    id: string;
    name: string;
    script: string;
    parent?: string;
    dataFields: any[];
    options: any[];
  }>> => {
    return apiClient.put(`/pdfmapper/doctype/${encodeURIComponent(id)}`, data);
  },
};
